<script setup>
  import { ref, watch, onMounted, defineAsyncComponent } from 'vue';
  import { useWindbowModelStorage } from '@/composables/useWindbowModelStorage';
  import DataContainer from "@/components/DataContainer.vue";
  import ModelSelector from "@/components/ModelSelector.vue";
  import GorgeWindbow from '@/components/GorgeWindbow.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";

  const GorgeWindbowUpgradeBlock = defineAsyncComponent(() => import('@/components/GorgeWindbowUpgradeBlock.vue'));

  // The windbow always describes the Columbia River Gorge; the selector looks
  // up available models at the legacy widget's western starting point.
  const WINDBOW_LAT = 46.15028;
  const WINDBOW_LON = -123.29751;
  const DEFAULT_MODEL_ID = 1;

  const emit = defineEmits(['containerSettings:change']);
  const windbow = defineModel('content');

  const props = defineProps({
    containerSettings: {
      type: Object,
      required: false
    },
    upgrade: {
      type: Array,
      required: true
    }
  });

  const windbowModelStorage = useWindbowModelStorage();

  const windbowViewType = ref('windbow');
  // Seeded from storage (or the webview query param); confirmed/defaulted once
  // the selector reports the available models.
  const selectedModelId = ref(windbowModelStorage.read());
  // The windbow only loads once a model id has been validated against the list.
  const activeModelId = ref(null);

  function getSettings() {
    return {
      windbowViewType: windbowViewType.value
    };
  }

  function emitSettingsChange() {
    emit('containerSettings:change');
  }

  // Mirrors the legacy ModelSelector defaulting: keep the saved model when it's
  // still offered, otherwise the windbow default (1), otherwise the first
  // accessible model.
  function resolveModelId(models) {
    const isAccessible = (model) => !model.upgrade_available;
    const find = (id) => models.find((model) => model.model_id === id && isAccessible(model));

    return (
      find(selectedModelId.value) ??
      find(DEFAULT_MODEL_ID) ??
      models.find(isAccessible) ??
      models[0]
    )?.model_id ?? null;
  }

  function handleModelsLoaded(models) {
    const modelId = resolveModelId(models);
    if (modelId == null) return;

    selectedModelId.value = modelId;
    activeModelId.value = modelId;
  }

  function handleModelSelected(modelId) {
    selectedModelId.value = modelId;
    activeModelId.value = modelId;
    windbowModelStorage.save(modelId);
  }

  watch(windbowViewType, (newValue) => {
    if (!windbow.value) return;

    windbow.value.changeViewType(newValue);
    emitSettingsChange();
  });

  onMounted(() => {
    if (props.containerSettings && Object.keys(props.containerSettings).length > 0) {
      windbowViewType.value = props.containerSettings.windbowViewType;
    };
  });

  defineExpose({ getSettings });
</script>

<template>
  <DataContainer v-if="props.upgrade[3] < 46" style="width: 100%; height: 100%; flex: 1;">
    <GorgeWindbowUpgradeBlock
      :upgrade="props.upgrade"
    />
  </DataContainer>
  <DataContainer v-else style="width: 100%; height: 100%; flex: 1;">
    <div class="jwx-data-container__controls">
      <div class="jwx-data-container__toolbar">
        <SegmentedControl
          v-model="windbowViewType"
          label="Select how you would like to view the Gorge Winbow data."
          style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px;"
        >
          <SegmentedControlButton value="windbow">Windbow</SegmentedControlButton>
          <SegmentedControlButton value="gradient">Gradient</SegmentedControlButton>
        </SegmentedControl>
      </div>
      <div class="jw-model-selector-container">
        <ModelSelector
          id="gorge-windbow-model-selector"
          :lat="WINDBOW_LAT"
          :lon="WINDBOW_LON"
          :analytics="{
            initiator: 'Gorge_Windbow',
            lat: WINDBOW_LAT,
            lon: WINDBOW_LON
          }"
          v-model="selectedModelId"
          @input:change="handleModelSelected"
          @models:loaded="handleModelsLoaded"
        />
      </div>
    </div>

    <GorgeWindbow
      v-model="windbow"
      v-model:viewType="windbowViewType"
      :modelId="activeModelId"
     />
  </DataContainer>
</template>
